import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProtectedRoute } from './routes/ProtectedRoute';

import LandingPage from './pages/LandingPage';
import ForgotPassword from './pages/auth/ForgotPassword';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import CompleteRegistration from './pages/auth/CompleteRegistration';
import Dashboard from './pages/staff/Dashboard';
import StaffProfile from './pages/staff/StaffProfile';
import MyReferrals from './pages/staff/MyReferrals';
import MyOpportunities from './pages/staff/MyOpportunities';
import AdminDashboard from './pages/admin/AdminDashboard';
import PendingStaff from './pages/admin/PendingStaff';
import PendingReferrals from './pages/admin/PendingReferrals';
import PendingIncentives from './pages/admin/PendingIncentives';
import StaffReview from './pages/admin/StaffReview';
import PendingCompanies from './pages/admin/PendingCompanies';
import CompanyHireRequests from './pages/admin/CompanyHireRequests';
import CompanyRegister from './pages/company/CompanyRegister';
import CompanyLogin from './pages/company/CompanyLogin';
import CompanyDashboard from './pages/company/CompanyDashboard';
import StaffSearch from './pages/company/StaffSearch';
import MyShortlist from './pages/company/MyShortlist';
import MyHireRequests from './pages/company/MyHireRequests';
import CompanyProfile from './pages/company/CompanyProfile';
import MyFacilities from './pages/company/MyFacilities';
import GenerateStaffingPlan from './pages/company/GenerateStaffingPlan';
import StaffingPlans from './pages/company/StaffingPlans';
import ViewStaffingPlan from './pages/company/ViewStaffingPlan';
import MyPostedOpportunities from './pages/company/MyPostedOpportunities';
import PostOpportunity from './pages/company/PostOpportunity';
import OpportunityApplicants from './pages/company/OpportunityApplicants';
import CompanyPlacements from './pages/company/CompanyPlacements';
import BrowseOpportunities from './pages/staff/BrowseOpportunities';
import MyPlacements from './pages/staff/MyPlacements';
import ActivePlacements from './pages/admin/ActivePlacements';
import AdminCompanyReview from './pages/admin/AdminCompanyReview';
import AllStaff from './pages/admin/AllStaff';
import AllCompanies from './pages/admin/AllCompanies';

function CompanyRoute({ children }) {
  const { isAuthenticated, isCompany, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" /></div>;
  if (!isAuthenticated) return <Navigate to="/company/login" />;
  if (!isCompany) return <Navigate to="/dashboard" />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Landing */}
          <Route path="/" element={<LandingPage />} />

          {/* Public — Staff/Admin */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Public — Company */}
          <Route path="/company/register" element={<CompanyRegister />} />
          <Route path="/company/login" element={<CompanyLogin />} />

          {/* Staff */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/complete-registration" element={<ProtectedRoute><CompleteRegistration /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><StaffProfile /></ProtectedRoute>} />
          <Route path="/referrals" element={<ProtectedRoute><MyReferrals /></ProtectedRoute>} />
          <Route path="/opportunities" element={<ProtectedRoute><MyOpportunities /></ProtectedRoute>} />

          {/* Admin */}
          <Route path="/admin/dashboard" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/pending-staff" element={<ProtectedRoute adminOnly><PendingStaff /></ProtectedRoute>} />
          <Route path="/admin/pending-referrals" element={<ProtectedRoute adminOnly><PendingReferrals /></ProtectedRoute>} />
          <Route path="/admin/pending-incentives" element={<ProtectedRoute adminOnly><PendingIncentives /></ProtectedRoute>} />
          <Route path="/admin/staff/:id/review" element={<ProtectedRoute adminOnly><StaffReview /></ProtectedRoute>} />
          <Route path="/admin/pending-companies" element={<ProtectedRoute adminOnly><PendingCompanies /></ProtectedRoute>} />
          <Route path="/admin/hire-requests" element={<ProtectedRoute adminOnly><CompanyHireRequests /></ProtectedRoute>} />

          {/* Company */}
          <Route path="/company/dashboard" element={<CompanyRoute><CompanyDashboard /></CompanyRoute>} />
          <Route path="/company/search" element={<CompanyRoute><StaffSearch /></CompanyRoute>} />
          <Route path="/company/shortlist" element={<CompanyRoute><MyShortlist /></CompanyRoute>} />
          <Route path="/company/hire-requests" element={<CompanyRoute><MyHireRequests /></CompanyRoute>} />
          <Route path="/company/profile" element={<CompanyRoute><CompanyProfile /></CompanyRoute>} />
          <Route path="/company/facilities" element={<CompanyRoute><MyFacilities /></CompanyRoute>} />
          <Route path="/company/staffing-plans" element={<CompanyRoute><StaffingPlans /></CompanyRoute>} />
          <Route path="/company/staffing-plans/generate" element={<CompanyRoute><GenerateStaffingPlan /></CompanyRoute>} />
          <Route path="/company/staffing-plans/:id" element={<CompanyRoute><ViewStaffingPlan /></CompanyRoute>} />
          <Route path="/company/opportunities" element={<CompanyRoute><MyPostedOpportunities /></CompanyRoute>} />
          <Route path="/company/opportunities/post" element={<CompanyRoute><PostOpportunity /></CompanyRoute>} />
          <Route path="/company/opportunities/:id/applicants" element={<CompanyRoute><OpportunityApplicants /></CompanyRoute>} />
          <Route path="/company/placements" element={<CompanyRoute><CompanyPlacements /></CompanyRoute>} />
          <Route path="/staff/opportunities" element={<ProtectedRoute><BrowseOpportunities /></ProtectedRoute>} />
          <Route path="/staff/placements" element={<ProtectedRoute><MyPlacements /></ProtectedRoute>} />
          <Route path="/admin/placements/active" element={<ProtectedRoute adminOnly><ActivePlacements /></ProtectedRoute>} />
          <Route path="/admin/company/:id/review" element={<ProtectedRoute adminOnly><AdminCompanyReview /></ProtectedRoute>} />
          <Route path="/admin/all-staff" element={<ProtectedRoute adminOnly><AllStaff /></ProtectedRoute>} />
          <Route path="/admin/all-companies" element={<ProtectedRoute adminOnly><AllCompanies /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>

        <ToastContainer position="top-right" autoClose={3000}
          hideProgressBar={false} closeOnClick pauseOnHover theme="colored" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
